const HOVER = 'a,button,summary,[role="button"],[role="link"],[role="menuitem"],[role="tab"],[role="switch"],.eco-card,.pf-card,.rm-item,.feature-card,.glass,[data-testid="token-bar"],[data-testid*="kbull"],[data-testid*="token-address"]';
const NATIVE = 'input,textarea,select,[contenteditable]:not([contenteditable="false"]),[role="textbox"],[role="slider"],iframe,video,audio,[data-native-cursor]';
const MAGNET = '.eco-card,.btn-neon,[data-cursor-magnetic]';
const clamp = (n, a, b) => Math.max(a, Math.min(b, n));

export const classifyTarget = (element) => {
  if (!(element instanceof Element) || element.closest(NATIVE)) return { native: true, hover: false };
  const text = element.textContent || '';
  return { native: false, hover: Boolean(element.closest(HOVER)) || (text.length < 80 && /\$KBULL\b/.test(text)) };
};

// Read/candidate caching is independent of cursor transforms. Existing elements
// are NEVER written to, moved, focused, clicked or assigned new event handlers.
export const createMagneticTargets = () => {
  let nodes = [], rects = [], dirtyNodes = true, dirtyRects = true, lastRead = -Infinity;
  const invalidate = () => { dirtyRects = true; };
  const observer = new MutationObserver((records) => {
    if (records.some(({ target }) => !(target instanceof Element) || !target.closest('[data-bull-target-layer]'))) {
      dirtyNodes = true;
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  const read = (now) => {
    if (dirtyNodes) {
      const candidates = new Set(document.querySelectorAll(MAGNET));
      document.querySelectorAll('a,button').forEach((el) => {
        if (/^(?:(?:BUY|GET)\s+\$KBULL\b|EXPLORE\b)/i.test(el.textContent.trim())) candidates.add(el);
      });
      nodes = [...candidates];
      dirtyNodes = false; dirtyRects = true;
    }
    if (dirtyRects || now - lastRead > 120) {
      rects = nodes.filter((el) => el.isConnected && !el.matches(':disabled')).map((el) => ({ el, r: el.getBoundingClientRect() }))
        .filter(({ r }) => r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < window.innerHeight && r.right > 0 && r.left < window.innerWidth);
      dirtyRects = false; lastRead = now;
    }
  };

  const pull = (point, target, now) => {
    read(now);
    const direct = target?.closest(MAGNET);
    let choice = rects.find(({ el }) => el === direct), distance = 0;
    if (!choice) {
      distance = 28;
      for (const entry of rects) {
        const { el, r } = entry;
        const x = clamp(point.x, r.left + 1, r.right - 1);
        const y = clamp(point.y, r.top + 1, r.bottom - 1);
        const d = Math.hypot(point.x - x, point.y - y);
        if (d >= distance) continue;
        const visible = document.elementFromPoint(x, y);
        if (visible && el.contains(visible)) { choice = entry; distance = d; }
      }
    }
    if (!choice) return { x: 0, y: 0 };
    const { r } = choice;
    const dx = r.left + r.width / 2 - point.x, dy = r.top + r.height / 2 - point.y;
    const length = Math.hypot(dx, dy);
    if (!length) return { x: 0, y: 0 };
    let amount = 3 * (1 - Math.min(distance, 28) / 28) ** 2 * Math.min(1, length / 60);
    // Outside the element, never pull the visual hotspot across its click edge.
    if (distance > 0) amount = Math.min(amount, distance * 0.5);
    return { x: dx / length * amount, y: dy / length * amount };
  };
  return { pull, invalidate, destroy: () => observer.disconnect() };
};